import React, { useState } from 'react';
import { Form, Button, Card, Alert, Container, Row, Col } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { FaPhone, FaLock, FaUserShield } from 'react-icons/fa';

interface AdminLoginFormValues {
  phoneNumber: string;
  password: string;
}

const AdminLogin: React.FC = () => {
  const { signIn, userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const navigate = useNavigate();

  const initialValues: AdminLoginFormValues = {
    phoneNumber: '',
    password: ''
  };

  const validationSchema = Yup.object({
    phoneNumber: Yup.string()
      .required('رقم الهاتف مطلوب')
      .matches(/^[0-9]+$/, 'رقم الهاتف يجب أن يحتوي على أرقام فقط'),
    password: Yup.string()
      .required('كلمة المرور مطلوبة')
  });

  const handleSubmit = async (
    values: AdminLoginFormValues,
    { setSubmitting }: FormikHelpers<AdminLoginFormValues>
  ) => {
    try {
      setLoading(true);
      setAdminError(null);
      
      // استخدام رقم الهاتف كبريد إلكتروني مؤقت للتسجيل
      const email = `${values.phoneNumber}@example.com`;
      
      // Sign in
      await signIn(email, values.password);
      
      // Check user role from userData directly
      if (userData?.role === UserRole.ADMIN) {
        navigate('/admin/dashboard');
      } else {
        setAdminError('ليس لديك صلاحيات للوصول إلى لوحة التحكم');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setAdminError('خطأ في تسجيل الدخول. تأكد من رقم الهاتف وكلمة المرور');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card className="auth-card">
            <Card.Header className="bg-primary text-white text-center py-4">
              <h2 className="mb-0">تسجيل دخول المسؤول</h2>
            </Card.Header>
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <div className="auth-icon-container">
                  <FaUserShield size={40} className="auth-icon" />
                </div>
                <h4 className="mt-3">مرحبًا بك في لوحة التحكم</h4>
                <p className="text-muted">قم بتسجيل الدخول للوصول إلى لوحة تحكم المسؤول</p>
              </div>
              
              {adminError && <Alert variant="danger">{adminError}</Alert>}
              
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ handleSubmit, isSubmitting }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4" controlId="phoneNumber">
                      <Form.Label className="fw-bold">رقم الهاتف</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaPhone className="text-primary" />
                        </span>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="phoneNumber"
                          placeholder="أدخل رقم الهاتف"
                          className="py-2"
                        />
                      </div>
                      <ErrorMessage
                        name="phoneNumber"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="password">
                      <Form.Label className="fw-bold">كلمة المرور</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock className="text-primary" />
                        </span>
                        <Field
                          as={Form.Control}
                          type="password"
                          name="password"
                          placeholder="أدخل كلمة المرور"
                          className="py-2"
                        />
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 py-2 mt-3 mb-3 d-flex align-items-center justify-content-center"
                      disabled={loading || isSubmitting}
                    >
                      {loading ? (
                        <>جاري تسجيل الدخول...</>
                      ) : (
                        <>
                          <FaUserShield className="me-2" />
                          تسجيل الدخول
                        </>
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>
              
              <div className="text-center mt-4 pt-3 border-top">
                <p className="mb-0">
                  <Link to="/" className="text-primary fw-bold text-decoration-none">العودة إلى الصفحة الرئيسية</Link>
                </p>
              </div>
            </Card.Body>
            <Card.Footer className="bg-light text-center py-3">
              <p className="text-muted mb-0"> {new Date().getFullYear()} منصة السامي التعليمية. جميع الحقوق محفوظة</p>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminLogin;
